package com.eladfinish.bible.contest.ui.components.bottomSheet

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.ModalBottomSheetProperties
import androidx.compose.material3.SheetState
import androidx.compose.material3.SheetValue
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.analytics.AnalyticsConstants
import com.eladfinish.bible.contest.close
import com.eladfinish.bible.contest.colorPrimaryDark
import com.eladfinish.bible.contest.strokeColor
import com.eladfinish.bible.contest.ui.common.PreviewTheme
import com.eladfinish.bible.contest.ui.utils.MyBackHandler
import dev.icerock.moko.resources.StringResource
import dev.icerock.moko.resources.compose.colorResource
import dev.icerock.moko.resources.compose.stringResource
import kotlinx.coroutines.launch
import kotlin.math.abs

// Fixed colors: a feature sheet keeps its parchment look in the dark theme as well.
private val SheetBackground = Color(0xFFFFF8E1)
private val Ink = Color(0xFF3E2723)
private val FooterGrey = Color(0xFF757575)
private val TagBackground = Color(0x33DAA000)

/** How the user closed a one-time feature sheet without its button. */
enum class FeatureSheetDismissMethod(val analyticsValue: String) {
    X(AnalyticsConstants.FeatureSheet.METHOD_X),
    SWIPE(AnalyticsConstants.FeatureSheet.METHOD_SWIPE),
    BACK(AnalyticsConstants.FeatureSheet.METHOD_BACK),
    OUTSIDE(AnalyticsConstants.FeatureSheet.METHOD_OUTSIDE),
}

/**
 * The window of a one-time "what's new" sheet. Closes with the X, a swipe down, Back or a tap
 * outside, and reports which one in [onDismissed] once it has slid away; the button reports
 * [onCtaClicked] instead, also after the sheet has slid away.
 *
 * [content] is the sheet's layout (usually a [FeatureSheetContent]), given the two callbacks that
 * close the sheet with its animation.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun FeatureDiscoverySheet(
    onDismissed: (FeatureSheetDismissMethod) -> Unit,
    onCtaClicked: () -> Unit,
    content: @Composable (onClose: () -> Unit, onCta: () -> Unit) -> Unit,
) {
    val currentOnDismissed by rememberUpdatedState(onDismissed)
    val currentOnCtaClicked by rememberUpdatedState(onCtaClicked)
    val scope = rememberCoroutineScope()

    // How the sheet is closing, decided at the first sign of it; null while it is open.
    val closing = remember { mutableStateOf<FeatureSheetClosing?>(null) }
    // Where the sheet rests once open: a swipe moves it from there, a tap outside does not.
    val restingOffset = remember { mutableStateOf<Float?>(null) }
    val sheetStateHolder = remember { mutableStateOf<SheetState?>(null) }

    val sheetState = rememberModalBottomSheetState(
        skipPartiallyExpanded = true,
        confirmValueChange = { target ->
            // Both a swipe and a tap outside pass here before the sheet hides (so do the X, Back
            // and the button, which have already set their reason).
            if (target == SheetValue.Hidden && closing.value == null) {
                val offset = sheetStateHolder.value?.let { runCatching { it.requireOffset() }.getOrNull() }
                val rest = restingOffset.value
                val moved = offset != null && rest != null && abs(offset - rest) > 0.5f
                closing.value = FeatureSheetClosing.Dismissed(
                    if (moved) FeatureSheetDismissMethod.SWIPE else FeatureSheetDismissMethod.OUTSIDE,
                )
            }
            true
        },
    )
    sheetStateHolder.value = sheetState

    LaunchedEffect(sheetState) {
        snapshotFlow { sheetState.currentValue == SheetValue.Expanded && sheetState.targetValue == SheetValue.Expanded }
            .collect { isAtRest ->
                if (isAtRest && restingOffset.value == null) {
                    restingOffset.value = runCatching { sheetState.requireOffset() }.getOrNull()
                }
            }
    }

    fun finish(reason: FeatureSheetClosing) {
        when (reason) {
            is FeatureSheetClosing.Dismissed -> currentOnDismissed(reason.method)
            FeatureSheetClosing.Cta -> currentOnCtaClicked()
        }
    }

    fun close(reason: FeatureSheetClosing) {
        if (closing.value != null) return
        closing.value = reason
        scope.launch { sheetState.hide() }.invokeOnCompletion { finish(reason) }
    }

    ModalBottomSheet(
        // A swipe or a tap outside, after the sheet has slid away.
        onDismissRequest = {
            finish(closing.value ?: FeatureSheetClosing.Dismissed(FeatureSheetDismissMethod.OUTSIDE))
        },
        sheetState = sheetState,
        containerColor = SheetBackground,
        contentColor = Ink,
        // Back is handled below, so that it can be told apart from a tap outside.
        properties = ModalBottomSheetProperties(shouldDismissOnBackPress = false),
    ) {
        // The sheet is a window of its own, outside the app's navigation and its back handling.
        MyBackHandler {
            close(FeatureSheetClosing.Dismissed(FeatureSheetDismissMethod.BACK))
        }
        Box(modifier = Modifier.navigationBarsPadding()) {
            content(
                { close(FeatureSheetClosing.Dismissed(FeatureSheetDismissMethod.X)) },
                { close(FeatureSheetClosing.Cta) },
            )
        }
    }
}

private sealed interface FeatureSheetClosing {
    data class Dismissed(val method: FeatureSheetDismissMethod) : FeatureSheetClosing
    data object Cta : FeatureSheetClosing
}

/**
 * The layout of a feature sheet: the header (the [tag] and the X) and the button with its
 * [footer] stay put, the [body] between them scrolls when the screen is short (iPhone SE), so the
 * button is always reachable.
 */
@Composable
fun FeatureSheetContent(
    tag: StringResource,
    cta: StringResource,
    footer: StringResource,
    onClose: () -> Unit,
    onCtaClicked: () -> Unit,
    modifier: Modifier = Modifier,
    body: @Composable ColumnScope.() -> Unit,
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .padding(start = 20.dp, end = 20.dp, bottom = 16.dp),
    ) {
        // In RTL the tag sits top-right and the X top-left.
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = stringResource(tag),
                modifier = Modifier
                    .background(color = TagBackground, shape = RoundedCornerShape(50))
                    .padding(horizontal = 12.dp, vertical = 4.dp),
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.Bold,
                fontSize = 15.sp,
                color = Ink,
            )
            Spacer(modifier = Modifier.weight(1f))
            IconButton(onClick = onClose) {
                Icon(
                    imageVector = Icons.Default.Close,
                    contentDescription = stringResource(SharedRes.strings.close),
                    tint = Ink,
                )
            }
        }

        Column(
            modifier = Modifier
                .weight(weight = 1f, fill = false)
                .verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            body()
            Spacer(modifier = Modifier.height(4.dp))
        }

        Spacer(modifier = Modifier.height(12.dp))
        // The home grid's button look: brown with the gold border.
        Button(
            onClick = onCtaClicked,
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(min = 60.dp)
                .border(
                    width = 1.dp,
                    color = colorResource(SharedRes.colors.strokeColor),
                    shape = RoundedCornerShape(12.dp),
                ),
            shape = RoundedCornerShape(12.dp),
            colors = ButtonDefaults.buttonColors(
                containerColor = colorResource(SharedRes.colors.colorPrimaryDark),
                contentColor = Color.White,
            ),
            elevation = ButtonDefaults.buttonElevation(defaultElevation = 0.dp, pressedElevation = 8.dp),
        ) {
            Text(
                text = stringResource(cta),
                style = MaterialTheme.typography.labelLarge,
                fontSize = 22.sp,
                fontWeight = FontWeight.Bold,
            )
        }
        Text(
            text = stringResource(footer),
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 8.dp),
            style = MaterialTheme.typography.bodySmall,
            fontSize = 13.sp,
            color = FooterGrey,
            textAlign = TextAlign.Center,
        )
    }
}

@Composable
fun FeatureSheetTitle(title: StringResource) {
    Text(
        text = stringResource(title),
        style = MaterialTheme.typography.headlineSmall,
        fontWeight = FontWeight.Bold,
        color = colorResource(SharedRes.colors.colorPrimaryDark),
    )
}

@Composable
fun FeatureSheetIntro(intro: StringResource) {
    Text(
        text = stringResource(intro),
        style = MaterialTheme.typography.bodyLarge,
        fontSize = 17.sp,
        color = Ink,
    )
}

/** One point of the sheet: a bold [lead] followed by its [body]. */
@Composable
fun FeatureSheetPoint(lead: StringResource, body: StringResource) {
    val leadText = stringResource(lead)
    val bodyText = stringResource(body)
    Text(
        text = buildAnnotatedString {
            withStyle(SpanStyle(fontWeight = FontWeight.Bold)) { append(leadText) }
            append(" ")
            append(bodyText)
        },
        style = MaterialTheme.typography.bodyLarge,
        fontSize = 16.sp,
        color = Ink,
    )
}

/** The frame the previews of a [FeatureSheetContent] sit in: a sheet's rounded top on its parchment. */
@Composable
fun FeatureSheetPreviewFrame(content: @Composable () -> Unit) {
    PreviewTheme {
        Box(
            modifier = Modifier
                .background(SheetBackground, RoundedCornerShape(topStart = 28.dp, topEnd = 28.dp))
                .padding(top = 16.dp),
        ) {
            content()
        }
    }
}
